import { useState } from 'react';
import { ChevronDown, Pause, Play, RotateCcw, Users, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Prompt 7: Undo / Cancel / Auto-draft all / Pause / Resume, collapsed into one menu.
 *
 * These are destructive and commissioner-only, and today they occupy the toolbar position
 * where the user's own draft action belongs. Rendered ONLY for the commissioner -- a
 * disabled button or an empty slot would still spend the space this move is reclaiming.
 *
 * The two irreversible items get confirmation dialogs. Undo and pause/resume do not: undo is
 * itself the correction for a mis-pick, and putting a dialog in front of it makes the fix
 * slower than the mistake.
 *
 * NOTE: this component does not implement pause/resume, only invokes the caller's handler.
 * nfl-pause-draft carries SAB-841's MIN_RESUME_SECONDS floor (30s, clamped to the pick
 * timer), which is what stops a pause taken near 0:00 from stranding the draft permanently.
 * Nothing here may bypass that path.
 *
 * ── EVERY ITEM IS GATED ON A RUNNING DRAFT ──────────────────────────────────
 *
 * All four act on a draft in flight, and the server refuses all four otherwise -- undo on a
 * completed draft comes back "Draft is not in progress." So the wiring was already right and
 * the AFFORDANCE was wrong: the menu offered four actions that could only fail.
 *
 * DISABLED, NOT HIDDEN. The menu is the commissioner's map of what exists; an item that
 * silently vanishes reads as a broken build, and they go looking for it. A disabled item
 * carrying its reason teaches the rule instead. (The MENU ITSELF still disappears for a
 * non-commissioner -- that is a different question, about whose menu this is, and it stays.)
 *
 * ── THE REASON IS RENDERED, NOT HOVERED ─────────────────────────────────────
 *
 * It was originally a `title` attribute, which is unreachable on precisely the elements
 * that need it: DropdownMenuItem carries `data-[disabled]:pointer-events-none`, so a
 * disabled item receives no pointer events, the browser never registers a hover, and the
 * native tooltip never fires. The reason was wired to a mechanism a disabled element cannot
 * trigger -- greyed out with no explanation, which is the state this was meant to prevent.
 *
 * Hover is the wrong channel here regardless. This ships inside a Capacitor app where
 * hover does not exist at all, and Radix skips disabled items in keyboard navigation, so a
 * focus-announced description would not be reachable either. The reason is therefore
 * VISIBLE TEXT inside the item: no interaction of any kind is required to read it, on any
 * input device. aria-describedby ties it to the item for assistive tech; `title` is kept as
 * a harmless desktop extra, not as the mechanism.
 */

export interface CommishMenuProps {
  isCommissioner: boolean;
  isPaused: boolean;
  /**
   * The league's draft_status. Anything other than in_progress/paused disables every item.
   * Optional so existing callers keep compiling, but a caller that omits it gets the
   * permissive old behaviour, so pass it.
   */
  draftStatus?: string | null;
  /**
   * Whether any pick has actually been made. Undo needs a pick to undo; the live room has
   * always guarded this (NFLDraftPage.tsx: `!picks.some(p => p.player_id)`) and this menu
   * did not.
   */
  hasMadePicks?: boolean;
  onPauseResume: (action: 'pause' | 'resume') => void;
  onUndoLastPick: () => void;
  onCancelDraft: () => void;
  onAutoDraftAll: () => void;
  busy?: boolean;
}

type Confirm = null | 'cancel' | 'autoAll';

/**
 * One menu item, with its reason rendered beneath the label when it is disabled.
 *
 * Deliberately one component rather than four call sites: the reason and the disabled state
 * have to stay in lockstep, and the failure mode of drift here is an item that greys out
 * while saying nothing -- the exact bug being fixed.
 */
function Item({
  testId, icon, label, reason, className, onSelect,
}: {
  testId: string;
  icon: React.ReactNode;
  label: string;
  /** Present means DISABLED, and this is why. */
  reason?: string;
  className?: string;
  onSelect: () => void;
}) {
  const disabled = reason !== undefined;
  const reasonId = `${testId}-reason`;
  return (
    <DropdownMenuItem
      data-testid={testId}
      disabled={disabled}
      aria-describedby={disabled ? reasonId : undefined}
      title={reason}
      onSelect={onSelect}
      className={cn('flex-col items-start gap-0.5', className)}
    >
      <span className="flex items-center">
        {icon}
        {label}
      </span>
      {disabled && (
        <span
          id={reasonId}
          data-testid={reasonId}
          className="pl-6 text-[10.5px] leading-snug text-muted-foreground"
        >
          {reason}
        </span>
      )}
    </DropdownMenuItem>
  );
}

export default function CommishMenu({
  isCommissioner, isPaused, onPauseResume, onUndoLastPick, onCancelDraft, onAutoDraftAll, busy,
  draftStatus, hasMadePicks = true,
}: CommishMenuProps) {
  const [confirm, setConfirm] = useState<Confirm>(null);

  // Non-commissioners get no button AND no empty slot.
  if (!isCommissioner) return null;

  // `undefined` means the caller did not tell us; treat that as running rather than
  // disabling everything for a caller that has not been updated yet.
  const draftRunning =
    draftStatus === undefined || draftStatus === 'in_progress' || draftStatus === 'paused';

  // Pending reads differently from completed and deserves its own sentence -- "has not
  // started" is actionable (go start it), "is over" is not.
  const stateWord =
    draftStatus === 'completed' ? 'The draft is over.'
    : draftStatus === 'pending' || draftStatus == null ? 'The draft has not started.'
    : 'The draft is not running.';

  const reason = (verb: string) => `${stateWord} ${verb}`;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="commish-menu"
            variant="outline"
            disabled={busy}
            className="h-8 gap-1.5 rounded-lg border-primary/40 bg-primary/10 px-3 font-display text-[11px] font-bold tracking-[0.06em] text-primary hover:bg-primary/15"
          >
            COMMISH
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <Item
            testId="commish-pause"
            icon={isPaused ? <Play className="mr-2 h-4 w-4" /> : <Pause className="mr-2 h-4 w-4" />}
            label={isPaused ? 'Resume draft' : 'Pause draft'}
            reason={draftRunning ? undefined : reason('There is nothing to pause.')}
            onSelect={() => onPauseResume(isPaused ? 'resume' : 'pause')}
          />
          <Item
            testId="commish-undo"
            icon={<RotateCcw className="mr-2 h-4 w-4" />}
            label="Undo last pick"
            reason={
              !draftRunning ? reason('Picks can only be undone while it is running.')
              : !hasMadePicks ? 'No picks have been made yet.'
              : undefined
            }
            onSelect={onUndoLastPick}
          />
          <DropdownMenuSeparator />
          <Item
            testId="commish-autodraft-all"
            icon={<Users className="mr-2 h-4 w-4" />}
            label="Auto-draft all teams"
            reason={draftRunning ? undefined : reason('There are no picks left to fill.')}
            className="text-gold focus:text-gold"
            onSelect={() => setConfirm('autoAll')}
          />
          <Item
            testId="commish-cancel"
            icon={<XCircle className="mr-2 h-4 w-4" />}
            label="Cancel draft"
            reason={draftRunning ? undefined : reason('There is nothing to cancel.')}
            className="text-damage focus:text-damage"
            onSelect={() => setConfirm('cancel')}
          />
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirm !== null} onOpenChange={o => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="commish-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === 'cancel' ? 'Cancel this draft?' : 'Auto-draft every remaining pick?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'cancel'
                ? 'Every pick made so far is discarded and the league returns to pre-draft. This cannot be undone.'
                : 'The server fills every remaining pick for every team immediately. Managers lose the rest of their draft. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep drafting</AlertDialogCancel>
            <AlertDialogAction
              data-testid="commish-confirm-go"
              onClick={() => {
                const which = confirm;
                setConfirm(null);
                if (which === 'cancel') onCancelDraft();
                if (which === 'autoAll') onAutoDraftAll();
              }}
              className="bg-damage text-white hover:bg-damage/90"
            >
              {confirm === 'cancel' ? 'Cancel draft' : 'Auto-draft all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
